import { NavigationGuard } from "vue-router"
import { BenefitTypeEnum } from "@vitable/asclepius-api-client-ts"
import { useEnrollmentStore } from "@/modules/enrollment-new/stores/enrollment-new-store"
import { useUserStore } from "@/store/user-store"
import { isEmbeddedContext } from "@common/services/embed-service"
import { VPC_STEP } from "@common/utilities/benefit-utils"
import { isPlanRecommendationsEnabled } from "@/config"
import {
  ENROLLMENT_STEP_ROUTES,
  ENROLLMENT_SUBFLOW_ROUTES,
  isRoutableBenefitStep,
  PLAN_SHOPPING_STEP,
  type EnrollmentStepKey,
  type NonVpcBenefitElection
} from "@/modules/enrollment-new/enrollment-flow-types"
import { hasIchraEnrollment, isMedicareEligible } from "@/modules/enrollment-new/enrollment-flow-helpers"
import {
  buildEnrollmentSegments,
  priorBenefitsCompleteOrRedirect,
  type EnrollmentSegment
} from "@/modules/enrollment-new/enrollment-segments"
import { tryParseNumberQueryParam } from "@/modules/enrollment-new/router/route-utils"

const exitRoute = () => ({ name: isEmbeddedContext() ? "embed-employee-dashboard" : "home" })

export const ensurePlanRecommendationsEnabled: NavigationGuard = (_to, _from, _next) => {
  if (isPlanRecommendationsEnabled) return true

  const medicalYears = useEnrollmentStore()
    .activeFlowElectionsByBenefitTypeAndYear(BenefitTypeEnum.Medical)
    .map((g) => g.year)
  if (medicalYears.length === 0) return exitRoute()
  return { name: ENROLLMENT_SUBFLOW_ROUTES.chooseHealthPlan, params: { year: String(medicalYears[0]) } }
}

export const ensureActiveEnrollmentSequence: NavigationGuard = (_to, _from, _next) => {
  const enrollmentStore = useEnrollmentStore()

  if (!enrollmentStore.isActiveEnrollmentFlowStillValid()) {
    return exitRoute()
  }

  return true
}

export const ensureActiveVpcOrNonVpcBenefitElection: NavigationGuard = (_to, _from, _next) => {
  const enrollmentStore = useEnrollmentStore()
  const electionsPackage = enrollmentStore.activeFlowElectionsPackage

  if (!electionsPackage || !(electionsPackage.vpcElection || electionsPackage.nonVpcElections.length > 0)) {
    return exitRoute()
  }

  return true
}

export const ensureVpcElectionPresent: NavigationGuard = (_to, _from, _next) => {
  const enrollmentStore = useEnrollmentStore()
  const electionsPackage = enrollmentStore.activeFlowElectionsPackage

  if (!electionsPackage?.vpcElection) {
    return { name: ENROLLMENT_STEP_ROUTES["sign-forms"] }
  }

  return true
}

export const ensureAllElectionsMade: NavigationGuard = (_to, _from, _next) => {
  const enrollmentStore = useEnrollmentStore()

  if (!enrollmentStore.activeFlowAllNonVpcElectionsElectedIn) {
    return { name: ENROLLMENT_STEP_ROUTES.background }
  }

  return true
}

function medicalElectionsForRequestedYear(yearParam: unknown): NonVpcBenefitElection[] {
  const year = tryParseNumberQueryParam(yearParam)
  if (year === undefined) return []
  return (
    useEnrollmentStore()
      .activeFlowElectionsByBenefitTypeAndYear(BenefitTypeEnum.Medical)
      .find((g) => g.year === year)?.elections ?? []
  )
}

export const ensureEligibleForChoosingHealthPlan: NavigationGuard = (to, _from, _next) => {
  if (!hasIchraEnrollment(medicalElectionsForRequestedYear(to.params.year))) {
    return exitRoute()
  }
  return true
}

export const ensureEligibleForSchedulingMedicareCall: NavigationGuard = (to, _from, _next) => {
  if (!hasIchraEnrollment(medicalElectionsForRequestedYear(to.params.year))) {
    return exitRoute()
  }

  if (!isMedicareEligible(useUserStore().member)) {
    return exitRoute()
  }

  return true
}

function buildSegmentsForGuard(): EnrollmentSegment[] {
  const enrollmentStore = useEnrollmentStore()

  const electionsByType: Record<string, NonVpcBenefitElection[]> = {}
  for (const step of enrollmentStore.activeFlowAvailableBenefitTypes) {
    if (step === VPC_STEP) continue
    electionsByType[step] = enrollmentStore.activeFlowElectionsByBenefitTypeAndYear(step).flatMap((g) => g.elections)
  }

  return buildEnrollmentSegments({
    availableBenefitTypes: enrollmentStore.activeFlowAvailableBenefitTypes,
    electionsByType,
    vpcElection: enrollmentStore.activeFlowElectionsPackage?.vpcElection,
    query: {},
    medicareEligibleIn: isMedicareEligible(useUserStore().member)
  })
}

export const ensureSequentialBenefitTypeCompletion: NavigationGuard = (to, _from, _next) => {
  const enrollmentStore = useEnrollmentStore()
  const requestedStepKey = to.meta.stepKey
  const requestedBenefitType = to.meta.benefitType
  const requestedYear = tryParseNumberQueryParam(to.params.year)

  if (requestedStepKey === PLAN_SHOPPING_STEP) {
    if (requestedYear === undefined) {
      return { name: ENROLLMENT_STEP_ROUTES["sign-forms"] }
    }
    return ensurePriorBenefitTypesComplete(buildSegmentsForGuard(), PLAN_SHOPPING_STEP, requestedYear)
  }

  if (!requestedBenefitType) {
    return true
  }

  if (requestedBenefitType === VPC_STEP) {
    return ensurePriorBenefitTypesComplete(buildSegmentsForGuard(), VPC_STEP)
  }

  if (!isRoutableBenefitStep(requestedBenefitType)) return true

  const electionsByYear = enrollmentStore.activeFlowElectionsByBenefitTypeAndYear(requestedBenefitType)
  const availableYears = electionsByYear.map((g) => g.year)

  if (requestedYear === undefined || !availableYears.includes(requestedYear)) {
    if (availableYears.length > 0) {
      return { name: ENROLLMENT_STEP_ROUTES[requestedBenefitType], params: { year: String(availableYears[0]) } }
    }
    return { name: ENROLLMENT_STEP_ROUTES["sign-forms"] }
  }

  return ensurePriorBenefitTypesComplete(buildSegmentsForGuard(), requestedBenefitType, requestedYear)
}

/**
 * Returns true when every benefit segment strictly before the requested step is complete,
 * or the route to redirect to (the first incomplete segment).
 *
 * `requestedYear` lets the medical-page case enforce "all prior years within this type are also
 * complete" — without a year it just means "all prior types done."
 */
export function ensurePriorBenefitTypesComplete(
  segments: ReadonlyArray<EnrollmentSegment>,
  requestedStep: EnrollmentStepKey,
  requestedYear?: number
): true | { name: string; params?: Record<string, string> } {
  const targetIndex = segments.findIndex((s) => {
    if (s.kind === "fixed" || s.barGroupKey !== requestedStep) return false
    if (requestedYear === undefined) return true
    const yearParam = (s.route as { params?: { year?: string } }).params?.year
    return yearParam === String(requestedYear)
  })

  // For VPC (no year), or when the requested year isn't in the segments, fall back to
  // the first segment with this barGroupKey (or end-of-list, which causes a no-op walk).
  const checkUpTo =
    targetIndex === -1 ? segments.findIndex((s) => s.kind !== "fixed" && s.barGroupKey === requestedStep) : targetIndex

  const result = priorBenefitsCompleteOrRedirect(segments, checkUpTo === -1 ? segments.length : checkUpTo)
  if (result === true) return true
  // Guard redirects historically carry only name + params; drop query so route equality stays clean.
  const { name, params } = result as { name: string; params?: Record<string, string> }
  return params ? { name, params } : { name }
}
